import React, { useState } from 'react';
import { PeakPrioritiesLogo } from './ui/PeakPrioritiesLogo';
import { Link, useLocation } from 'react-router-dom';
import { scrollToTop } from '../utils/scrollUtils';
import {
  Layers,
  LayoutGrid,
  Search,
  GitBranch,
  Database,
  Settings,
  SearchCode,
  BookOpen,
  Library,
  Palette,
  Menu,
  X,
  Mountain,
  Map,
  Play,
  Target,
  Users,
  FlaskConical,
  ChevronDown,
  Boxes,
  Code,
  Sun,
  Moon,
  Compass,
} from 'lucide-react';

interface NavigationProps {
  onSearchClick?: () => void;
  mode?: 'design' | 'runtime';
  onModeChange?: (mode: 'design' | 'runtime') => void;
  elevationTheme?: 'light' | 'dark';
  onElevationThemeChange?: (t: 'light' | 'dark') => void;
}

// Custom Link component that scrolls to top on navigation
const ScrollToTopLink: React.FC<{ 
  to: string; 
  className?: string; 
  title?: string; 
  children: React.ReactNode;
  onClick?: () => void;
}> = ({ to, className, title, children, onClick }) => {
  const handleClick = () => {
    // Call the provided onClick handler (e.g., for mobile menu close)
    if (onClick) {
      onClick();
    }
    
    // Only scroll to top if we're navigating to a different page
    if (window.location.pathname !== to) {
      setTimeout(scrollToTop, 100); // Small delay to ensure navigation happens first
    }
  };

  return (
    <Link to={to} className={className} title={title} onClick={handleClick}>
      {children}
    </Link>
  );
};

export const Navigation: React.FC<NavigationProps> = ({ onSearchClick, mode = 'design', onModeChange: _onModeChange, elevationTheme, onElevationThemeChange }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [templateConstructorHover, setTemplateConstructorHover] = useState(false);

  const isPrioritizationRoute = location.pathname.startsWith('/prioritization');
  const isPlanningRoute = location.pathname.startsWith('/planning');
  const isProductRoute = isPrioritizationRoute || isPlanningRoute;

  // Prevent body scroll when mobile menu is open
  React.useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Template Constructor submenu items
  const templateConstructorItems = [
    { path: '/elevation/grammar', label: 'Metadata Schema', icon: Database, layer: 'M3' },
    { path: '/elevation/models', label: 'Construct Registry', icon: Search, layer: 'M2' },
    { path: '/elevation/templates', label: 'Template Registry', icon: GitBranch, layer: 'M1' },
    { path: '/elevation/runtime', label: 'Records', icon: GitBranch, layer: 'M0' },
  ];

  const designNavItems = [
    { 
      path: null, 
      label: 'Template Constructor', 
      icon: Boxes,
      submenu: templateConstructorItems,
      isDropdown: true
    },
    { path: '/elevation/engines', label: 'Platform Engines', icon: Settings },
    { path: '/elevation/operations', label: 'Operations', icon: Code },
  ];

  const runtimeNavItems = [
    { path: '/', label: 'Dashboard', icon: Layers },
    { path: '/apps', label: 'Apps', icon: LayoutGrid },
    { path: '/priorities', label: 'My Priorities', icon: Target },
    { path: '/decisions', label: 'Decision Log', icon: BookOpen },
    { path: '/rhythm', label: 'My Rhythm', icon: Play },
    { path: '/assessments', label: 'Assessments', icon: Search },
    { path: '/elevation/team', label: 'Team', icon: Users },
  ];

  const navItems = mode === 'design' ? designNavItems : runtimeNavItems;


         const topUtilityItems = [
           { path: '/elevation/method', label: 'The Method', icon: Compass, color: 'primary' },
           { path: '/elevation/lab', label: 'Lab', icon: FlaskConical, color: 'amber' },
           { path: '/elevation/site-map', label: import.meta.env.DEV ? 'Site Map & Dev Servers' : 'Site Map', icon: Map, color: 'primary' },
           { path: '/elevation/documentation-hub', label: 'Documentation', icon: Library, color: 'primary' },
           { path: '/elevation/glossary', label: 'Glossary', icon: BookOpen, color: 'primary' },
         ];


  const isActive = (path: string | null) => {
    if (!path) return false;
    return location.pathname === path;
  };

  // Check if any Template Constructor submenu item is active
  const isTemplateConstructorActive = templateConstructorItems.some(item => isActive(item.path));

  return (
    <>
      {/* Top Utility Bar - Desktop Only */}
      <div className="hidden md:block bg-[var(--p-shade)] border-b border-[var(--p-hairline)]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-10">
            {/* Peak Priorities Logo */}
            <div className="flex items-center">
              <PeakPrioritiesLogo height={20} className="text-[#3D4B5C] dark:text-[var(--text-pri)]" />
            </div>

            <div className="flex items-center space-x-4">

            {/* Mode Links: Product (Prioritization | Planning) when in product area; otherwise Design | Runtime */}
            <div className="flex items-center gap-3 mr-2">
            {isProductRoute ? (
              <>
                <Link
                  to="/prioritization"
                  className={`text-sm md:text-base transition-colors cursor-pointer ${isPrioritizationRoute ? 'font-semibold text-[var(--p-ink)]' : 'font-normal text-[var(--p-ink-soft)] hover:text-[var(--p-ink)]'}`}
                  title="Prioritization - Cycles, Items, Published Lists"
                >
                  Prioritization
                </Link>
                <Link
                  to="/planning"
                  className={`text-sm md:text-base transition-colors cursor-pointer ${isPlanningRoute ? 'font-semibold text-[var(--p-ink)]' : 'font-normal text-[var(--p-ink-soft)] hover:text-[var(--p-ink)]'}`}
                  title="Planning - Work Graph, Capacity, Milestones"
                >
                  Planning
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/elevation/design"
                  className={`text-sm md:text-base transition-colors cursor-pointer ${location.pathname === '/elevation/design' ? 'font-semibold text-[var(--p-ink)]' : 'font-normal text-[var(--p-ink-soft)] hover:text-[var(--p-ink)]'}`}
                  title="Design - Content Workflow & Knowledge Architecture"
                >
                  Design
                </Link>
                <Link
                  to="/elevation/app"
                  className="text-sm md:text-base transition-colors cursor-pointer font-normal text-[var(--p-ink-soft)] hover:text-[var(--p-ink)]"
                  title="Runtime - Execution & Tools"
                >
                  Runtime
                </Link>
              </>
            )}
              <span className="text-[var(--p-hairline-2)]">|</span>
              {/* Local dev builds only: the Site Map opens with the "Local dev servers" panel (every server you can start, with
                  Start buttons). The panel and the API behind it exist only in `npm run dev`, so production gets no link. */}
              {import.meta.env.DEV && (
                <>
                  <Link
                    to="/elevation/site-map"
                    className={`text-sm md:text-base transition-colors cursor-pointer ${location.pathname === '/elevation/site-map' ? 'font-semibold text-[var(--p-ink)]' : 'font-normal text-[var(--p-ink-soft)] hover:text-[var(--p-ink)]'}`}
                    title="Dev Servers - every local server you can start (Site Map)"
                  >
                    Dev Servers
                  </Link>
                  <span className="text-[var(--p-hairline-2)]">|</span>
                </>
              )}
            </div>

            {/* Top Utility Items */}
            {topUtilityItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`p-1.5 rounded transition-colors ${
                  isActive(path)
                    ? 'text-primary-600'
                    : 'text-[var(--p-ink-soft)] hover:text-[var(--p-ink)]'
                }`}
                title={label}
              >
                <Icon className="h-5 w-5" />
              </Link>
            ))}
            <button
              onClick={onSearchClick}
              className="p-1.5 rounded transition-colors text-[var(--p-ink-soft)] hover:text-[var(--p-ink)] hover:bg-[var(--hover-tint)]"
              title="Search (⌘K)"
            >
              <SearchCode className="h-5 w-5" />
            </button>
            {onElevationThemeChange && (
              <button
                onClick={() => onElevationThemeChange(elevationTheme === 'dark' ? 'light' : 'dark')}
                className="p-1.5 rounded transition-colors text-[var(--p-ink-soft)] hover:text-[var(--p-ink)] hover:bg-[var(--hover-tint)]"
                title={elevationTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {elevationTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
            )}
            
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation - Hidden when in product area (Prioritization or Planning) */}
      {!isProductRoute && (
        <nav className="bg-[var(--p-paper)] border-b border-[var(--p-hairline)]">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link to="/elevation/home" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                <Mountain className="h-6 w-6 md:h-8 md:w-8 text-primary-600 flex-shrink-0" />
                <h1 className="text-base md:text-2xl font-heading font-semibold text-[var(--p-ink)] whitespace-nowrap">
                  <span className="md:hidden">Elevation</span>
                  <span className="hidden md:inline">Elevation</span>
                </h1>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex space-x-1">
                {navItems.map((item) => {
                  // Handle dropdown items (Template Constructor)
                  if (item.isDropdown && item.submenu) {
                    return (
                      <div
                        key={item.label}
                        className="relative"
                        onMouseEnter={() => setTemplateConstructorHover(true)}
                        onMouseLeave={() => setTemplateConstructorHover(false)}
                      >
                        <div
                          className={`
                            flex items-center space-x-2 px-4 py-2 rounded-none text-sm font-medium transition-colors cursor-pointer
                            ${isTemplateConstructorActive
                              ? 'text-primary-700 bg-primary-50'
                              : 'text-[var(--p-ink-soft)] hover:text-primary-600 hover:bg-[var(--p-shade)]'
                            }
                          `}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.label}</span>
                          <ChevronDown className={`h-3 w-3 transition-transform ${templateConstructorHover ? 'rotate-180' : ''}`} />
                        </div>
                        
                        {/* Dropdown Menu */}
                        {templateConstructorHover && (
                          <div className="absolute top-full left-0 mt-1 w-64 bg-[var(--p-paper)] border border-[var(--p-hairline-2)] py-2 z-50">
                            {item.submenu.map((subItem) => (
                              <ScrollToTopLink
                                key={subItem.path}
                                to={subItem.path}
                                className={`
                                  flex items-center space-x-3 px-4 py-2 text-sm transition-colors
                                  ${isActive(subItem.path)
                                    ? 'text-primary-700 bg-primary-50'
                                    : 'text-[var(--p-ink-soft)] hover:text-primary-600 hover:bg-[var(--p-shade)]'
                                  }
                                `}
                              >
                                <subItem.icon className="h-4 w-4" />
                                <div className="flex-1">
                                  <div className="font-medium">{subItem.label}</div>
                                  <div className="text-xs text-[var(--p-ink-soft)]">{subItem.layer}</div>
                                </div>
                              </ScrollToTopLink>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Regular nav items
                  if (!item.path) return null;
                  return (
                    <ScrollToTopLink
                      key={item.path}
                      to={item.path}
                      className={`
                        flex items-center space-x-2 px-4 py-2 rounded-none text-sm font-medium transition-colors
                        ${isActive(item.path)
                          ? 'text-primary-700 bg-primary-50'
                          : 'text-[var(--p-ink-soft)] hover:text-primary-600 hover:bg-[var(--p-shade)]'
                        }
                      `}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </ScrollToTopLink>
                  );
                })}

                {/* Design Language Link */}
                <Link
                  to="/elevation/design-language"
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-none text-sm font-medium transition-colors
                    ${isActive('/design-language')
                      ? 'text-consulting-slate-blue-700 bg-consulting-slate-blue-50'
                      : 'text-[var(--p-ink-soft)] hover:text-consulting-slate-blue-600 hover:bg-[var(--p-shade)]'
                    }
                  `}
                >
                  <Palette className="h-4 w-4" />
                  <span>Design Language</span>
                </Link>


              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-none text-[var(--p-ink-soft)] hover:text-[var(--p-ink)] hover:bg-[var(--hover-tint)]"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

        {/* Mobile Menu */}
        {!isPrioritizationRoute && mobileMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-16 bottom-0 bg-[var(--bg-raised)] z-40 overflow-y-auto border-t border-[var(--p-hairline)]">
            <div className="container mx-auto px-4 py-4 space-y-1">
              {/* Main Nav Items */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-[var(--p-ink-soft)] uppercase tracking-wide mb-2">Main Navigation</p>
                {navItems.map((item) => {
                  // Handle dropdown items (Template Constructor) in mobile
                  if (item.isDropdown && item.submenu) {
                    return (
                      <div key={item.label} className="mb-2">
                        <div className="px-4 py-2 text-xs font-semibold text-[var(--p-ink-soft)] uppercase tracking-wide">
                          {item.label}
                        </div>
                        {item.submenu.map((subItem) => (
                          <ScrollToTopLink
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`
                              flex items-center space-x-3 px-4 py-3 rounded-none text-sm font-medium transition-colors ml-4
                              ${isActive(subItem.path)
                                ? 'text-primary-700 bg-primary-50'
                                : 'text-[var(--p-ink-soft)] hover:text-primary-600 hover:bg-[var(--p-shade)]'
                              }
                            `}
                          >
                            <subItem.icon className="h-5 w-5" />
                            <div className="flex-1">
                              <div>{subItem.label}</div>
                              <div className="text-xs text-[var(--p-ink-soft)]">{subItem.layer}</div>
                            </div>
                          </ScrollToTopLink>
                        ))}
                      </div>
                    );
                  }
                  
                  // Regular nav items
                  if (!item.path) return null;
                  return (
                    <ScrollToTopLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`
                        flex items-center space-x-3 px-4 py-3 rounded-none text-sm font-medium transition-colors
                        ${isActive(item.path)
                          ? 'text-primary-700 bg-primary-50'
                          : 'text-[var(--p-ink-soft)] hover:text-primary-600 hover:bg-[var(--p-shade)]'
                        }
                      `}
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{item.label}</span>
                    </ScrollToTopLink>
                  );
                })}
                {/* Design Language Link */}
                <Link
                  to="/elevation/design-language"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-none text-sm font-medium transition-colors
                    ${isActive('/design-language')
                      ? 'text-consulting-slate-blue-700 bg-consulting-slate-blue-50'
                      : 'text-[var(--p-ink-soft)] hover:text-consulting-slate-blue-600 hover:bg-[var(--p-shade)]'
                    }
                  `}
                >
                  <Palette className="h-5 w-5" />
                  <span>Design Language</span>
                </Link>


              </div>



              {/* Utility Items */}
              <div>
                <p className="text-xs font-semibold text-[var(--p-ink-soft)] uppercase tracking-wide mb-2">Tools & Resources</p>
                {topUtilityItems.map(({ path, label, icon: Icon }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      flex items-center space-x-3 px-4 py-3 rounded-none text-sm font-medium transition-colors
                      ${isActive(path)
                        ? 'text-primary-700 bg-primary-50'
                        : 'text-[var(--p-ink-soft)] hover:text-primary-600 hover:bg-[var(--p-shade)]'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{label}</span>
                  </Link>
                ))}
                <button
                  onClick={() => {
                    onSearchClick?.();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-none text-sm font-medium text-[var(--p-ink-soft)] hover:text-primary-600 hover:bg-[var(--p-shade)] transition-colors"
                >
                  <SearchCode className="h-5 w-5" />
                  <span>Search (⌘K)</span>
                </button>
              </div>
            </div>
          </div>
        )}
        </nav>
      )}
    </>
  );
};
